import { useCallback, useEffect, useState } from 'react'

import { api, ApiError } from '../api'
import { ensureGender, mixedFree } from '../gender'
import { formatWhen, t } from '../i18n'
import {
  canShareCard,
  confirmAction,
  copyText,
  haptic,
  notify,
  openExternal,
  shareCard,
  shareToChat,
  useBackButton,
} from '../telegram'
import GameChat from '../components/GameChat'
import InviteSheet from '../components/InviteSheet'
import CourtSpots from '../components/CourtSpots'
import { isTournamentKind } from '../types'
import type { Booking, BookingClub, Club, Game, GameInvite, PlayerMe, Slot } from '../types'
import type { BookTarget } from './Booking'
import Coaches from './Coaches'
import MatchResults from './MatchResults'
import TournamentView from './TournamentView'

/** Поиск по названию и адресу точнее примерных координат: Google Maps
 *  находит карточку самого клуба с входом и парковкой. */
function mapsUrl(club: Club): string | null {
  if (club.maps_url) return club.maps_url
  const query = club.address
    ? `${club.name}, ${club.address}`
    : club.lat != null && club.lon != null
      ? `${club.lat},${club.lon}`
      : null
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null
}

/** WhatsApp только для испанских мобильных (6xx/7xx): стационарный номер
 *  клуба в WhatsApp не откроется. */
function whatsappUrl(phone: string | null): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  const local = digits.startsWith('34') ? digits.slice(2) : digits
  return /^[67]\d{8}$/.test(local) ? `https://wa.me/34${local}` : null
}

interface Props {
  gameId: number
  me: PlayerMe
  onBack: () => void
  /** Заявка на корт под эту игру (клуб с бронированием через Por4, v0.35) */
  onBook?: (target: BookTarget) => void
  /** Поверх открыто окно брони — системная «Назад» принадлежит ему */
  covered?: boolean
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

function Avatar({ slot }: { slot: Slot }) {
  return slot.player.photo_url ? (
    <img className="avatar" style={{ marginLeft: 0 }} src={slot.player.photo_url} alt="" />
  ) : (
    <span className="avatar" style={{ marginLeft: 0 }}>
      {initials(slot.player.first_name)}
    </span>
  )
}

function PlayerRow({
  slot,
  isCaptain,
  showPaid = false,
  onTogglePaid,
  showGender = false,
  onToggleGender,
  onRemove,
}: {
  slot: Slot
  isCaptain: boolean
  /** Капитан снимает игрока из состава или резерва */
  onRemove?: () => void
  /** Микст: метка М/Ж у каждого; организатор нажатием меняет */
  showGender?: boolean
  onToggleGender?: () => void
  /** Отметка взноса: видят все, переключает только организатор */
  showPaid?: boolean
  onTogglePaid?: () => void
}) {
  const p = slot.player
  return (
    <div className="row between" style={{ padding: '8px 0', gap: 8 }}>
      <div className="row" style={{ gap: 10, minWidth: 0, flex: 1 }}>
        <Avatar slot={slot} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>
            {p.first_name}
            {isCaptain && <span className="muted"> · {t().captain}</span>}
            {showGender &&
              (onToggleGender ? (
                <button className={`gender-mark ${p.gender ?? 'none'}`} onClick={onToggleGender}>
                  {p.gender === 'm' ? t().genderM : p.gender === 'f' ? t().genderF : '?'}
                </button>
              ) : (
                <span className={`gender-mark ${p.gender ?? 'none'}`}>
                  {p.gender === 'm' ? t().genderM : p.gender === 'f' ? t().genderF : '?'}
                </span>
              ))}
          </div>
          <div className="muted">
            {t().level} {p.level?.toFixed(2) ?? '—'} · {t()[p.side]}
          </div>
        </div>
      </div>
      {showPaid ? (
        onTogglePaid ? (
          <button
            className={`chip paid${slot.paid ? ' on' : ''}`}
            aria-pressed={slot.paid}
            onClick={onTogglePaid}
          >
            {slot.paid ? `✓ ${t().paidMark}` : t().notPaidMark}
          </button>
        ) : (
          <span className={slot.paid ? 'badge' : 'badge neutral'}>
            {slot.paid ? `✓ ${t().paidMark}` : t().notPaidMark}
          </span>
        )
      ) : p.reliability < 70 ? (
        <span className="badge warn">{t().lowReliability}</span>
      ) : (
        <span className="badge neutral">{p.reliability}%</span>
      )}
      {onRemove && (
        <button className="remove-player" aria-label={`${t().removePlayer}: ${p.first_name}`} onClick={onRemove}>
          ✕
        </button>
      )}
    </div>
  )
}

export default function GameDetail({ gameId, me, onBack, onBook, covered = false }: Props) {
  const [game, setGame] = useState<Game | null>(null)
  const [missing, setMissing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [invites, setInvites] = useState<GameInvite[]>([])
  const [chatting, setChatting] = useState(false)
  // Тренировка: страница тренера поверх экрана (id анкеты ищем по капитану)
  const [coachId, setCoachId] = useState<number | null>(null)
  // Перенос времени: открытая форма с датой и временем (локальными)
  const [moving, setMoving] = useState<{ date: string; time: string; clubId: number } | null>(null)
  const [clubs, setClubs] = useState<Club[]>([])
  // Бронь корта через Por4: клуб принимает заявки, и есть ли уже заявка под игру
  const [bookClub, setBookClub] = useState<BookingClub | null>(null)
  const [gameBooking, setGameBooking] = useState<Booking | null>(null)

  // Кого уже позвали — видит состав; остальным сервер вернёт пустой список
  const loadInvites = useCallback(async () => {
    try {
      setInvites(await api.gameInvites(gameId))
    } catch {
      /* не критично для экрана */
    }
  }, [gameId])

  const load = useCallback(async () => {
    try {
      setGame(await api.game(gameId))
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true)
      else notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [gameId])

  useEffect(() => {
    void load()
  }, [load])

  // Закрыли чат — перечитываем игру: счётчик непрочитанных обнулился
  const closeChat = useCallback(() => {
    setChatting(false)
    void load()
  }, [load])

  const closeCoach = useCallback(() => setCoachId(null), [])

  // Кнопка «назад» Telegram: из чата — на экран игры, с экрана игры — в ленту
  useEffect(() => {
    if (covered) return undefined
    return useBackButton(chatting ? closeChat : coachId !== null ? closeCoach : onBack)
  }, [chatting, closeChat, coachId, closeCoach, onBack, covered])

  // Капитану будущей игры в клубе с бронированием — кнопка «Запросить корт»
  const bookable = Boolean(
    game && game.is_captain && game.status !== 'cancelled' && new Date(game.starts_at).getTime() > Date.now() && onBook,
  )
  const bookClubId = game?.club.id
  useEffect(() => {
    if (!bookable || covered || bookClubId === undefined) return
    let alive = true
    void Promise.all([api.bookingClubs(), api.myBookings()]).then(
      ([list, mine]) => {
        if (!alive) return
        setBookClub(list.find((c) => c.id === bookClubId) ?? null)
        setGameBooking(mine.find((b) => b.game_id === gameId && (b.status === 'requested' || b.status === 'confirmed')) ?? null)
      },
      () => undefined,
    )
    if (!covered) void load()
    return () => {
      alive = false
    }
  }, [bookable, bookClubId, covered, gameId, load])

  async function openCoach() {
    if (!game) return
    try {
      const coach = (await api.coaches()).find((c) => c.player_id === game.captain.id)
      if (coach) setCoachId(coach.id)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const canInvite = game?.can_invite ?? false
  useEffect(() => {
    if (canInvite) void loadInvites()
  }, [canInvite, loadInvites, game?.joined_count])

  async function run(action: () => Promise<Game | void>) {
    setBusy(true)
    haptic('medium')
    try {
      const result = await action()
      if (result) setGame(result)
      else await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  if (missing) {
    return (
      <div className="card">
        <div className="title">{t().notFound}</div>
        <button className="ghost" style={{ marginTop: 12 }} onClick={onBack}>
          ← {t().feed}
        </button>
      </div>
    )
  }

  if (!game) return <p className="muted">{t().loading}</p>

  const joined = game.slots.filter((s) => s.status === 'joined')
  const waiting = game.slots.filter((s) => s.status === 'waitlist')
  const free = Math.max(game.max_players - game.joined_count, 0)
  const cancelled = game.status === 'cancelled'
  const tournament = isTournamentKind(game.kind)
  const training = game.kind === 'training'
  const running = game.status === 'running'
  const finished = game.status === 'finished'
  // После старта состав зафиксирован расписанием: запись и выход закрыты
  const locked = running || finished
  // Взнос отмечаем только там, где он есть
  const showPaid = (tournament || training) && game.price_per_player != null
  const mixedSpots = game.mixed ? mixedFree(game) : null
  const pendingInvites = game.can_invite ? invites.filter((i) => i.status === 'pending').length : 0
  const lo = game.level_min.toFixed(2)
  const hi = game.level_max.toFixed(2)

  async function share() {
    if (!game?.share_url) return notify('Ссылка недоступна: не задан BOT_USERNAME')
    haptic()
    // Карточка с кнопкой «Записаться» — основной путь. Ссылка — запасной:
    // старый клиент Telegram или ошибка на стороне Bot API.
    if (canShareCard()) {
      try {
        const prepared = await api.prepareShareCard(game.id)
        await shareCard(prepared.id)
        return
      } catch {
        /* падаем в обычную ссылку */
      }
    }
    shareToChat(game.share_url, t().shareText(formatWhen(game.starts_at), game.club.name, lo, hi, free))
  }

  async function primary() {
    if (!game) return
    if (game.my_status) {
      if (!(await confirmAction(t().leaveConfirm))) return
      await run(() => api.leave(game.id))
    } else {
      await joinNow()
    }
  }

  async function joinNow() {
    if (!game) return
    // В микст нужно знать, мужское место занимаем или женское
    if (game.mixed && !(await ensureGender(me))) return
    await run(() => api.join(game.id))
  }

  function openReschedule() {
    if (!game) return
    const d = new Date(game.starts_at)
    const pad = (n: number) => String(n).padStart(2, '0')
    setMoving({
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      clubId: game.club.id,
    })
    if (!clubs.length) api.clubs().then(setClubs, () => undefined)
  }

  async function saveReschedule() {
    if (!game || !moving) return
    const next = new Date(`${moving.date}T${moving.time}:00`)
    if (Number.isNaN(next.getTime())) return
    const timeChanged = Math.abs(next.getTime() - new Date(game.starts_at).getTime()) >= 60_000
    const clubChanged = moving.clubId !== game.club.id
    if (!timeChanged && !clubChanged) return setMoving(null)
    const clubName = clubs.find((c) => c.id === moving.clubId)?.name ?? game.club.name
    if (!(await confirmAction(t().changeGameConfirm(formatWhen(next.toISOString()), clubName)))) return
    await run(() =>
      api.updateGame(game.id, {
        ...(timeChanged ? { starts_at: next.toISOString() } : {}),
        ...(clubChanged ? { club_id: moving.clubId } : {}),
      }),
    )
    setMoving(null)
  }

  /** Текст + ссылка на нашем домене: в WhatsApp ссылка t.me часто открывает
   *  чат с ботом вместо игры — эта страница ведёт в игру надёжно. */
  function outsideText(): string {
    if (!game) return ''
    return `${t().shareText(formatWhen(game.starts_at), game.club.name, lo, hi, free)} ${game.web_url ?? game.share_url ?? ''}`
  }

  async function copyLink() {
    if (!game) return
    const ok = await copyText(game.web_url ?? game.share_url ?? '')
    haptic()
    notify(ok ? t().linkCopied : t().linkCopyFailed)
  }

  async function declineInvite() {
    if (!game?.my_invite_id) return
    const id = game.my_invite_id
    await run(() => api.declineInvite(id))
  }

  // Капитан снимает игрока: с подтверждением, потому что тому уйдёт сообщение
  function removable(slot: Slot): (() => void) | undefined {
    if (!game?.can_remove || slot.player.id === game.captain.id) return undefined
    return async () => {
      if (!(await confirmAction(t().removePlayerConfirm(slot.player.first_name)))) return
      await run(() => api.removePlayer(game.id, slot.player.id))
    }
  }

  async function cancelWholeGame() {
    if (!game || !(await confirmAction(t().cancelGameConfirm))) return
    await run(() => api.cancelGame(game.id))
  }

  const primaryLabel =
    game.my_status === 'joined'
      ? t().leave
      : game.my_status === 'waitlist'
        ? `${t().youAreWaiting} · ${t().leave.toLowerCase()}`
        : free > 0
          ? t().join
          : t().waitlist

  return (
    <div>
      <div style={{ margin: '4px 0 14px' }}>
        <div className="muted">{formatWhen(game.starts_at)}</div>
        <div className="big">{game.club.name}</div>
        <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {tournament && (
            <span className="badge gold">{game.mixed ? t().mixedBadge : t().tournamentBadge(game.kind)}</span>
          )}
          {training && <span className="badge gold">{t().trainingBadge}</span>}
          {game.kind === 'game' && !game.rated && <span className="badge neutral">{t().friendlyBadge}</span>}
          {cancelled ? (
            <span className="badge warn">{t().gameCancelled}</span>
          ) : finished ? (
            <span className="badge neutral">{t().statusFinished}</span>
          ) : running ? (
            <span className="badge neutral">{t().statusRunning}</span>
          ) : free > 0 ? (
            <span className="badge">
              {mixedSpots ? t().mixedFree(mixedSpots.m, mixedSpots.f) : t().freeSlots(free)}
            </span>
          ) : (
            <span className="badge neutral">{t().full}</span>
          )}
          <span className={game.court_booked ? 'badge' : 'badge warn'}>
            {game.court_booked ? t().courtBooked : t().courtNotBooked}
          </span>
        </div>
      </div>

      {game.invited_by && !game.my_status && !cancelled && !locked && (
        <div className="card invite-banner">
          <div className="title">{t().invitesYou(game.invited_by, game.kind)}</div>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button style={{ flex: 1 }} disabled={busy} onClick={joinNow}>
              {free > 0 ? t().inviteGo : t().inviteGoWaitlist}
            </button>
            <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={declineInvite}>
              {t().inviteNo}
            </button>
          </div>
        </div>
      )}

      {training && (
        <div className="card">
          <div className="row between" style={{ gap: 10 }}>
            <div className="row" style={{ gap: 10, minWidth: 0 }}>
              {game.captain.photo_url ? (
                <img className="avatar" style={{ marginLeft: 0 }} src={game.captain.photo_url} alt="" />
              ) : (
                <span className="avatar" style={{ marginLeft: 0 }}>
                  {initials(game.captain.first_name)}
                </span>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="muted small">{t().coachLabel}</div>
                <b>{game.captain.first_name}</b>
              </div>
            </div>
            {!game.is_captain && (
              <button className="ghost" style={{ flex: '0 0 auto' }} onClick={openCoach}>
                {t().coachAbout}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="row between">
          <span className="muted">{t().level}</span>
          <b>
            {lo}–{hi}
          </b>
        </div>
        <div className="row between" style={{ marginTop: 6 }}>
          <span className="muted">{t().duration}</span>
          <span>
            {game.duration_min} {t().minutes}
          </span>
        </div>
        {game.price_per_player != null && (
          <div className="row between" style={{ marginTop: 6 }}>
            <span className="muted">{t().price}</span>
            <span>
              {game.price_per_player} € {t().perPlayer}
            </span>
          </div>
        )}
        {game.notes && <div style={{ marginTop: 10 }}>{game.notes}</div>}
        {!game.fits_my_level && !game.my_status && !game.is_captain && (
          <div className="muted" style={{ marginTop: 10 }}>
            {t().levelWarning}
          </div>
        )}
      </div>

      {/* Турнир идёт — расписание и таблица важнее состава, поднимаем их наверх */}
      {tournament && locked && <TournamentView game={game} me={me} onGameChanged={load} />}

      {game.kind === 'game' && game.max_players === 4 && !cancelled && game.joined_count > 0 && (
        <div className="card">
          <CourtSpots
            game={game}
            me={me}
            busy={busy}
            onSet={(spot, playerId) => run(() => api.setSpot(game.id, spot, playerId))}
          />
        </div>
      )}

      <div className="card">
        <div className="title" style={{ marginBottom: 4 }}>
          {training ? t().students : t().roster} · {game.joined_count}/{game.max_players}
        </div>
        {joined.map((slot) => (
          <PlayerRow
            key={slot.id}
            slot={slot}
            isCaptain={slot.player.id === game.captain.id}
            showGender={game.mixed}
            onToggleGender={
              game.mixed && game.is_captain && !locked
                ? () => run(() => api.setPlayerGender(game.id, slot.player.id, slot.player.gender === 'm' ? 'f' : 'm'))
                : undefined
            }
            showPaid={showPaid}
            onTogglePaid={
              showPaid && game.is_captain
                ? () => run(() => api.setPaid(game.id, slot.id, !slot.paid))
                : undefined
            }
            onRemove={removable(slot)}
          />
        ))}
        {game.can_invite &&
          invites
            .filter((i) => i.status === 'pending')
            .map((i) => (
              <div key={`inv-${i.id}`} className="row" style={{ gap: 10, padding: '8px 0' }}>
                <span className="avatar invited" style={{ marginLeft: 0 }}>
                  {initials(i.invitee_name)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{i.invitee_name}</div>
                  <div className="muted small">{t().invitedWaiting(i.inviter_name)}</div>
                </div>
              </div>
            ))}
        {(mixedSpots
          ? [
              ...Array.from({ length: locked ? 0 : mixedSpots.m }, () => t().freeSpotM),
              ...Array.from({ length: locked ? 0 : mixedSpots.f }, () => t().freeSpotF),
            ]
          : Array.from({ length: locked ? 0 : Math.max(free - pendingInvites, 0) }, () => t().freeSpot)
        ).map((label, i) => (
          <div key={`free-${i}`} className="row" style={{ gap: 10, padding: '8px 0' }}>
            <span className="avatar empty" style={{ marginLeft: 0 }}>
              +
            </span>
            <span className="muted">{label}</span>
          </div>
        ))}
      </div>

      {tournament
        ? !cancelled && !locked && <TournamentView game={game} me={me} onGameChanged={load} />
        : !training && <MatchResults game={game} me={me} />}

      {waiting.length > 0 && (
        <div className="card">
          <div className="title" style={{ marginBottom: 4 }}>
            {t().waitlistTitle} · {waiting.length}
          </div>
          {waiting.map((slot) => (
            <PlayerRow key={slot.id} slot={slot} isCaptain={false} onRemove={removable(slot)} />
          ))}
        </div>
      )}

      {game.chat_available && (
        <div className="card">
          {game.pinned_text && (
            <div className="pinned-note">
              <div className="small chat-pinned-label">📌 {t().chatPinned}</div>
              <div className="chat-pinned-text">{game.pinned_text}</div>
            </div>
          )}
          <div className="row between" style={{ gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div className="title">
                💬 {training ? t().chatTraining : tournament ? t().chatTournament : t().chatGame}
                {game.chat_unread > 0 && (
                  <span className="badge unread" style={{ marginLeft: 8 }}>
                    {t().chatNew(game.chat_unread)}
                  </span>
                )}
              </div>
              <div className="muted small" style={{ marginTop: 2 }}>
                {t().chatHint}
              </div>
            </div>
            <button
              className={game.chat_unread > 0 ? '' : 'ghost'}
              style={{ flex: '0 0 auto' }}
              onClick={() => setChatting(true)}
            >
              {t().chatOpen}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="title">{game.club.name}</div>
        {game.club.address && <div className="muted">{game.club.address}</div>}
        {game.club.note && <div className="muted">{game.club.note}</div>}
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {mapsUrl(game.club) && (
            <button className="ghost" onClick={() => openExternal(mapsUrl(game.club)!)}>
              {t().route}
            </button>
          )}
          {game.club.booking_url && (
            <button className="ghost" onClick={() => openExternal(game.club.booking_url!)}>
              {t().clubBooking}
            </button>
          )}
          {whatsappUrl(game.club.phone) && (
            <button className="ghost" onClick={() => openExternal(whatsappUrl(game.club.phone)!)}>
              {t().whatsapp}
            </button>
          )}
        </div>
      </div>

      {!cancelled && (
        <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
          {game.removed_by_captain && !game.my_status && !game.my_invite_id && (
            <div className="card muted" style={{ margin: 0 }}>
              {t().removedNote}
            </div>
          )}
          {!game.is_captain && !locked && !(game.removed_by_captain && !game.my_status && !game.my_invite_id) && (
            <button
              className={game.my_status ? 'ghost' : ''}
              disabled={busy}
              onClick={primary}
            >
              {primaryLabel}
            </button>
          )}
          {game.can_invite && (
            <button disabled={busy} onClick={() => setInviting(true)}>
              {t().invite}
            </button>
          )}
          <button className="ghost" disabled={busy} onClick={share}>
            {t().share}
          </button>
          {(game.web_url || game.share_url) && (
            <div className="row" style={{ gap: 8 }}>
              <button
                className="ghost"
                style={{ flex: 1 }}
                onClick={() => openExternal(`https://wa.me/?text=${encodeURIComponent(outsideText())}`)}
              >
                {t().shareWhatsApp}
              </button>
              <button className="ghost" style={{ flex: 1 }} onClick={copyLink}>
                {t().copyLink}
              </button>
            </div>
          )}

          {game.is_captain && (
            <>
              {bookable && gameBooking?.status === 'requested' && (
                <div className="card muted" style={{ marginBottom: 0 }}>⏳ {t().gameBookPending}</div>
              )}
              {bookable && bookClub && !gameBooking && !game.court_booked && (
                <button
                  disabled={busy}
                  onClick={() =>
                    onBook?.({ clubId: bookClub.id, gameId: game.id, startsAt: game.starts_at, minutes: game.duration_min })
                  }
                >
                  {t().gameBookCourt(bookClub.name)}
                </button>
              )}
              <button
                className="ghost"
                disabled={busy}
                onClick={() =>
                  run(() => api.updateGame(game.id, { court_booked: !game.court_booked }))
                }
              >
                {game.court_booked ? t().markNotBooked : t().markBooked}
              </button>
              {/* У турнира время и клуб — в «Редактировать турнир» */}
              {!locked &&
                !tournament &&
                new Date(game.starts_at).getTime() > Date.now() &&
                (moving ? (
                  <div className="reschedule">
                    <select
                      aria-label={t().club}
                      style={{ marginBottom: 8 }}
                      value={moving.clubId}
                      onChange={(e) => setMoving({ ...moving, clubId: Number(e.target.value) })}
                    >
                      {(clubs.length ? clubs : [game.club]).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · {c.city}
                        </option>
                      ))}
                    </select>
                    <div className="row" style={{ gap: 8 }}>
                      <input
                        type="date"
                        aria-label={t().changeTime}
                        value={moving.date}
                        onChange={(e) => setMoving({ ...moving, date: e.target.value })}
                      />
                      <input
                        type="time"
                        aria-label={t().changeTime}
                        value={moving.time}
                        onChange={(e) => setMoving({ ...moving, time: e.target.value })}
                      />
                    </div>
                    <div className="muted small" style={{ margin: '8px 0' }}>
                      {t().changeTimeHint}
                    </div>
                    <div className="row" style={{ gap: 8 }}>
                      <button style={{ flex: 1 }} disabled={busy} onClick={saveReschedule}>
                        {t().save}
                      </button>
                      <button className="ghost" style={{ flex: 1 }} onClick={() => setMoving(null)}>
                        {t().doneClose}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="ghost" disabled={busy} onClick={openReschedule}>
                    {t().changeTime}
                  </button>
                ))}
              {!finished && (
                <button className="ghost" disabled={busy} onClick={cancelWholeGame}>
                  {t().cancelGame}
                </button>
              )}
            </>
          )}
        </div>
      )}
      {chatting && <GameChat game={game} onClose={closeChat} />}
      {coachId !== null && (
        <Coaches me={me} trainings={[]} initialCoachId={coachId} onClose={closeCoach} onOpenGame={closeCoach} />
      )}
      {inviting && (
        <InviteSheet game={game} me={me} onClose={() => setInviting(false)} onInvited={loadInvites} />
      )}
    </div>
  )
}
